import { useState } from 'react';
import Image from 'next/image';
import httpContext from 'express-http-context';
import { v4 as uuid } from 'uuid';
import { clsx } from 'clsx';
import imageLoader from '@/components/Scene7Image/image-loader';
import { publishClientLogs } from '@/components/client-logger';
import styles from './Scene7Image.module.scss';

type CrossOriginTypes = 'anonymous' | 'use-credentials' | '' | undefined;

const IMAGE_QUALITY = 70;

interface FallbackImage {
  src: string;
  width: number;
  height: number;
}

export interface Scene7ImageProps {
  src?: string;
  id?: string;
  alt: string;
  sizes: string;
  className?: string;
  'data-testid'?: string;
  crossOrigin?: CrossOriginTypes;
  fallbackImage: FallbackImage;
}

export function Scene7Image(props: Scene7ImageProps) {
  const {
    alt,
    className,
    crossOrigin,
    'data-testid': dataTestId,
    fallbackImage,
    id,
    sizes,
    src,
  } = props;

  const [hasImageError, setHasImageError] = useState<boolean>(false);

  const handleImageError = () => {
    setHasImageError(true);
    publishClientLogs({
      logData: {
        correlationId: (httpContext.get('correlation-id') ?? uuid()) as string,
        payload: {
          details: {
            browserMeta: JSON.stringify({
              browserName: navigator.appName,
              browserVersion: navigator.appVersion,
              pageUrl: window.location.href,
              userAgent: navigator.userAgent,
            }),
            description: 'Scene7 error, calls to Scene7 are failing.',
            imgSrc: src,
            serviceProvider: 'SCENE_SEVEN',
            severity: 'CRITICAL',
            stackTrace: undefined,
            time: new Date().toISOString(),
          },
          logType: 'EVENT',
          message: 'Frontend Library Error',
        },
      },
      type: 'error',
    })
      .then(() => {})
      .catch(() => {});
  };

  const cssList = className ? [styles.image, className] : [styles.image];

  return hasImageError || !src ? (
    <Image
      alt={alt}
      className={clsx(cssList)}
      height={fallbackImage.height}
      src={fallbackImage.src}
      unoptimized
      width={fallbackImage.width}
    />
  ) : (
    <Image
      alt={alt}
      className={clsx(cssList)}
      crossOrigin={crossOrigin}
      data-testid={dataTestId}
      fill
      id={id}
      loader={imageLoader}
      onError={handleImageError}
      quality={IMAGE_QUALITY}
      sizes={sizes}
      src={src}
    />
  );
}
